import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AccountConnectScreen } from "@/features/accounts/AccountConnectScreen";
import { AccountConnectModal } from "@/features/accounts/AccountConnectModal";
import { ComposeDrawer } from "@/features/compose/ComposeDrawer";
import { AddressBookModal } from "@/features/contacts/AddressBookModal";
import { MailShell } from "@/features/shell/MailShell";
import { MailShellHeader } from "@/features/shell/MailShellHeader";
import { MailShellHeaderActions } from "@/features/shell/MailShellHeaderActions";
import { InboxList } from "@/features/inbox/InboxList";
import { MailNavSidebar } from "@/features/inbox/MailNavSidebar";
import { FolderMobileSheet } from "@/features/folders/FolderMobileSheet";
import { MailCreateFolderDialog } from "@/features/folders/MailCreateFolderDialog";
import { MailRenameFolderDialog } from "@/features/folders/MailRenameFolderDialog";
import { MailConfirmDialog } from "@/features/shared/MailConfirmDialog";
import { MessageReader } from "@/features/message/MessageReader";
import { MessageReaderBackBar } from "@/features/message/MessageReaderBackBar";
import { OutboxMessageReader } from "@/features/outbox/OutboxMessageReader";
import { ScheduledSendReader } from "@/features/outbox/ScheduledSendReader";
import { useComposeActions, type DraftFolderRefreshSpec } from "@/hooks/useComposeActions";
import { isModalComposeSession } from "@/lib/compose/composePlacement";
import { useCollectMailContacts } from "@/hooks/useCollectMailContacts";
import { MailContactsProvider } from "@/hooks/useMailContacts";
import { useInboxBulkActions } from "@/hooks/useInboxBulkActions";
import { useInboxListDisplay } from "@/hooks/useInboxListDisplay";
import { useMailSearchDisplay } from "@/hooks/useMailSearchDisplay";
import { useMailAccounts } from "@/hooks/useMailAccounts";
import { useMailAppBoot } from "@/hooks/useMailAppBoot";
import { MailBootScreen } from "@/features/boot/MailBootScreen";
import { useOrderedMailAccounts } from "@/hooks/useOrderedMailAccounts";
import { useMailFolders } from "@/hooks/useMailFolders";
import { useExpandedAccountsFolders } from "@/hooks/useExpandedAccountsFolders";
import { useMailInboxSync } from "@/hooks/useMailInboxSync";
import { useMailRefresh, type MailFolderInvalidation } from "@/hooks/useMailRefresh";
import { useMailSnooze } from "@/hooks/useMailSnooze";
import { useSnoozedListRows } from "@/hooks/useSnoozedListRows";
import { useMailDueImapRefresh } from "@/hooks/useMailDueImapRefresh";
import { useMessageLabels } from "@/hooks/useMessageLabels";
import { useMailBootstrap } from "@/hooks/useMailBootstrap";
import { useUnifiedInbox } from "@/hooks/useUnifiedInbox";
import { useMultAccountInboxPrefetch } from "@/hooks/useMultAccountInboxPrefetch";
import { useInboxHeadersSessionHydrate } from "@/hooks/useInboxHeadersSessionHydrate";
import { useWarmStandardFolderHeaders } from "@/hooks/useWarmStandardFolderHeaders";
import { hydrateInboxHeadersFromSession } from "@/lib/mail/inboxHeadersSessionHydrate";
import { useMultiAccountAttachmentFlags } from "@/hooks/useMultiAccountAttachmentFlags";
import { useMultAccountInboxBackgroundSync } from "@/hooks/useMultAccountInboxBackgroundSync";
import { isFolderStorageSyncActive } from "@/lib/storage/folderStorageProgress";
import { useAutoFolderHeadersPrefetch } from "@/hooks/useAutoFolderHeadersPrefetch";
import { useMailWorkerPresence } from "@/hooks/useMailWorkerPresence";
import { useMailMutationActive } from "@/hooks/useMailMutationActive";
import { MailFolderStorageDialog } from "@/features/folders/MailFolderStorageDialog";
import { notifyMailStorageStatsRefresh } from "@/features/accounts/MailStorageQuotaSection";
import { useMailMessage } from "@/hooks/useMailMessage";
import { useMailSendQueue } from "@/hooks/useMailSendQueue";
import { scheduledSendsToSummaries, useMailScheduledSends } from "@/hooks/useMailScheduledSends";
import { MailAccountSettingsModal } from "@/features/accounts/MailAccountSettingsModal";
import type { AccountSettingsFocus } from "@/features/accounts/accountSettingsFocus";
import { deleteMailAccount } from "@/lib/api/accounts";
import { fetchMailFolders } from "@/lib/api/imap";
import { clearAccountClientMailCache } from "@/lib/mail/accountMailCache";
import {
  filterAccountsForMailSync,
  isGoogleOAuthMailAccount,
} from "@/lib/accounts/googleOAuthAccounts";
import { isMicrosoftOAuthMailAccount } from "@/lib/accounts/microsoftOAuthAccounts";
import { clearMailDeepLinkFromUrl, readMailDeepLink } from "@/lib/mail/mailDeepLink";
import { readAccountSettings } from "@/lib/mail/accountSettingsPrefs";
import { clearFolderSnapshot } from "@/lib/mail/inboxFolderBus";
import { syncFolderHeaders, syncFolderFromServerCache, warmFolderMessages } from "@/lib/mail/folderWarmup";
import { getAccountDisplayLabel } from "@/lib/mail/accountDisplayLabel";
import {
  applyInboxListFilter,
  INBOX_FILTER_DEFAULT,
  inboxClientFilterIsActive,
  splitInboxListFilter,
  type InboxListFilter,
} from "@/lib/mail/messageLabels";
import {
  readInboxListFilter,
  writeInboxListFilter,
} from "@/lib/mail/inboxListFilterPrefs";

import { MailContextMenu } from "@/features/shared/MailContextMenu";
import { MailMoveFolderDialog } from "@/features/shared/MailMoveFolderDialog";
import { CoffreDestFolderPickerModal } from "@/features/message/CoffreDestFolderPickerModal";
import { BulkAttachmentProgressModal } from "@/features/message/BulkAttachmentProgressModal";
import { SnoozeModal } from "@/features/snooze/SnoozeModal";
import {
  AURELIA_SNOOZED_FOLDER,
  isSnoozedNavFolder,
  SNOOZED_FOLDER_LABEL,
  type MailSnoozeWoke,
} from "@/lib/snooze/constants";
import { isSnoozedListFolder, resolveSnoozedImapFolder } from "@/lib/snooze/snoozeNavFolder";
import {
  AURELIA_OUTBOX_FOLDER,
  isOutboxNavFolder,
  SCHEDULED_OUTBOX_LABEL,
} from "@/lib/outbox/constants";
import {
  AURELIA_UNIFIED_INBOX_FOLDER,
  isUnifiedInboxNavFolder,
  UNIFIED_INBOX_LABEL,
} from "@/lib/unified/constants";
import { flattenFoldersForPicker } from "@/lib/folders/folderTree";
import { subscribeFolderSnapshotsChanged } from "@/lib/mail/inboxFolderBus";
import {
  getTotalUnreadHold,
  subscribeUnreadHold,
} from "@/lib/mail/mailUnreadHold";
import { clearBodiesWarming } from "@/lib/mail/mailBodyWarmState";
import {
  countUnreadInFolderSnapshot,
  mergeFoldersWithSnapshotUnread,
  sumInboxUnreadFromFolders,
} from "@/lib/mail/folderSnapshotUnread";
import { isDraftsNavFolder, navFolderSlot, resolveDraftsImapFolder, resolveSentImapFolder, resolveTrashImapFolder, DRAFTS_SAVING_FOLDER_LABEL } from "@/lib/mail/folderNavSections";
import { ensureSentFolderOverride } from "@/lib/mail/sentFolderSetup";
import {
  criticalFolderSlotsNeedAttention,
  hasSeenFolderImapNudge,
  markFolderImapNudgeSeen,
} from "@/lib/mail/folderSlotImapStatus";
import { showMailToast } from "@/lib/ui/mailToast";
import { playMailNotificationSound } from "@/lib/mail/mailNotificationSound";
import {
  MAIL_SNOOZE_WOKE_EVENT,
  type MailSnoozeWokeDetail,
} from "@/lib/snooze/mailSnoozeWakeEvents";
import type { OutboxEnqueuePayload, OutboxEnqueueResult } from "@/lib/outbox/outboxQueue";
import { activeOutboxItems, readOutboxQueue } from "@/lib/outbox/outboxQueue";
import { mergeOutboxSummaries } from "@/lib/outbox/outboxDisplay";
import {
  resolveOutboxNavLabel,
  type AccountOutboxState,
} from "@/lib/outbox/outboxNavRows";
import {
  getScheduledSendsCache,
  getScheduledSendsCachedCount,
  removeScheduledSendPlaceholders,
  subscribeScheduledSendsCacheChanged,
  upsertScheduledSendCache,
} from "@/lib/outbox/scheduledSendsSessionCache";
import {
  getSnoozeCachedCount,
  subscribeSnoozeCacheChanged,
} from "@/lib/snooze/snoozeSessionCache";
import { type PendingDraftSave } from "@/lib/compose/draftSavePending";
import type { MailMessageSummary, MailAccountSummary, MailFolder } from "@/lib/types/mail";
import { deleteMailFolder } from "@/lib/api/imap";

const SEARCH_DEBOUNCE_MS = 350;

function findMessageRowForReader(
  uid: number,
  lists: {
    displayMessages: MailMessageSummary[];
    labeledMessages: MailMessageSummary[];
    inboxMessages?: MailMessageSummary[];
  },
  accountId?: number | null
): MailMessageSummary | null {
  const matchAccount = (row: MailMessageSummary) =>
    accountId == null || row.accountId == null || row.accountId === accountId;

  return (
    lists.displayMessages.find((m) => m.uid === uid && matchAccount(m)) ??
    lists.labeledMessages.find((m) => m.uid === uid && matchAccount(m)) ??
    lists.inboxMessages?.find((m) => m.uid === uid && matchAccount(m)) ??
    null
  );
}

function resolveAccountOutboxState(
  accountId: number,
  activeAccountId: number | undefined,
  queueCount: number,
  sendingCount: number,
  failedCount: number
): AccountOutboxState {
  if (accountId === activeAccountId) {
    return {
      queueCount,
      sendingCount,
      failedCount,
      scheduledCount: getScheduledSendsCachedCount(accountId),
    };
  }

  const items = activeOutboxItems(readOutboxQueue(), accountId);
  return {
    queueCount: items.length,
    sendingCount: items.filter(
      (item) => item.status === "sending" || item.status === "pending"
    ).length,
    failedCount: items.filter((item) => item.status === "failed").length,
    scheduledCount: getScheduledSendsCachedCount(accountId),
  };
}

/** Programmation en cours (POST / placeholder local) — pas l’envoi SMTP. */
function accountSchedulingPending(
  accountId: number,
  schedulePendingAccountId?: number | null
): boolean {
  if (schedulePendingAccountId === accountId) return true;
  return (getScheduledSendsCache(accountId) ?? []).some((entry) => entry.id < 1);
}

/** Envoi en cours visible pour n’importe quel compte (cache + file locale). */
function accountOutboxSendingBusy(
  accountId: number,
  options: {
    activeAccountId?: number;
    schedulePendingAccountId?: number | null;
    activeScheduledSendingCount?: number;
    activeQueueSendingCount?: number;
  }
): boolean {
  if (accountSchedulingPending(accountId, options.schedulePendingAccountId)) return true;

  if (accountId === options.activeAccountId) {
    if ((options.activeScheduledSendingCount ?? 0) > 0) return true;
    if ((options.activeQueueSendingCount ?? 0) > 0) return true;
  }

  const cached = getScheduledSendsCache(accountId) ?? [];
  const now = Date.now();
  if (
    cached.some((entry) => {
      if (entry.status !== "pending" || entry.id < 1) return false;
      const t = new Date(entry.sendAt).getTime();
      return Number.isFinite(t) && t <= now;
    })
  ) {
    return true;
  }

  const items = activeOutboxItems(readOutboxQueue(), accountId);
  return items.some((item) => item.status === "sending" || item.status === "pending");
}

function MailContactsCollector({
  messages,
  readerFrom,
  readerTo,
}: {
  messages: MailMessageSummary[];
  readerFrom?: string | null;
  readerTo?: string | null;
}) {
  useCollectMailContacts(messages, readerFrom, readerTo);
  return null;
}

export function MailWorkspace() {
  const { accounts, loading: accountsLoading, error: accountsError, hasAccount, reload, applyAccountOrder, patchAccount, removeAccount } =
    useMailAccounts();
  const { orderedAccounts, moveAccount } = useOrderedMailAccounts(accounts, {
    onApplyOrder: applyAccountOrder,
    onReorderFailed: () => void reload(),
  });
  const syncableAccounts = useMemo(
    () => filterAccountsForMailSync(orderedAccounts),
    [orderedAccounts]
  );
  const pausedImapAccountIds = useMemo(() => {
    const ids = new Set<number>();
    for (const account of accounts) {
      if (isGoogleOAuthMailAccount(account) || isMicrosoftOAuthMailAccount(account)) ids.add(account.id);
    }
    return ids;
  }, [accounts]);
  const [disconnecting, setDisconnecting] = useState(false);
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [createFolderAccountId, setCreateFolderAccountId] = useState<number | null>(null);
  const [renameFolderOpen, setRenameFolderOpen] = useState(false);
  const [renameFolderAccountId, setRenameFolderAccountId] = useState<number | null>(null);
  const [renameFolderTarget, setRenameFolderTarget] = useState<MailFolder | null>(null);
  const [deleteFolderOpen, setDeleteFolderOpen] = useState(false);
  const [deleteFolderAccountId, setDeleteFolderAccountId] = useState<number | null>(null);
  const [deleteFolderTarget, setDeleteFolderTarget] = useState<MailFolder | null>(null);
  const [deleteFolderBusy, setDeleteFolderBusy] = useState(false);
  const [deleteFolderError, setDeleteFolderError] = useState<string | null>(null);
  const [folderStorageOpen, setFolderStorageOpen] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [expandedAccountIds, setExpandedAccountIds] = useState<Set<number>>(() => new Set());
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [selectedUid, setSelectedUid] = useState<number | null>(null);
  const [selectedReaderAccountId, setSelectedReaderAccountId] = useState<number | null>(null);
  const [selectedFolder, setSelectedFolder] = useState("INBOX");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [settingsAccountId, setSettingsAccountId] = useState<number | null>(null);
  const [settingsFocus, setSettingsFocus] = useState<AccountSettingsFocus>("navigation");
  const [settingsVersion, setSettingsVersion] = useState(0);
  const [composeOpen, setComposeOpen] = useState(false);
  const [draftSavePending, setDraftSavePending] = useState(false);
  const [draftSavePendingAccountId, setDraftSavePendingAccountId] = useState<number | null>(null);
  const [schedulePendingAccountId, setSchedulePendingAccountId] = useState<number | null>(null);
  const [pendingDraftSaves, setPendingDraftSaves] = useState<PendingDraftSave[]>([]);
  const [listFilter, setListFilter] = useState<InboxListFilter>(INBOX_FILTER_DEFAULT);
  const [addressBookOpen, setAddressBookOpen] = useState(false);
  const [readingExpanded, setReadingExpanded] = useState(false);

  const displayMessagesRef = useRef<MailMessageSummary[]>([]);
  const selectedFolderRef = useRef(selectedFolder);
  selectedFolderRef.current = selectedFolder;
  const invalidateFoldersRef = useRef<(spec: MailFolderInvalidation) => void>(() => {});
  const connectBootstrapRef = useRef<number | null>(null);
  const removeInboxMessageRef = useRef<(uid: number) => void>(() => {});
  const onEnqueueRef = useRef<(payload: OutboxEnqueuePayload) => OutboxEnqueueResult | void>(() => {});

  const active = useMemo(() => {
    const pool = syncableAccounts.length > 0 ? syncableAccounts : accounts;
    if (selectedAccountId != null) {
      return (
        pool.find((a) => a.id === selectedAccountId) ??
        pool.find((a) => a.isDefault) ??
        pool[0]
      );
    }
    return pool.find((a) => a.isDefault) ?? pool[0];
  }, [accounts, syncableAccounts, selectedAccountId]);

  const imapAccountId = useMemo(() => {
    if (active?.id != null && pausedImapAccountIds.has(active.id)) return undefined;
    return active?.id;
  }, [active?.id, pausedImapAccountIds]);

  /** Avant useMailInboxSync — sinon initialSnap liste = null au 1er render. */
  useInboxHeadersSessionHydrate(syncableAccounts, { enabled: hasAccount });
  useWarmStandardFolderHeaders(syncableAccounts, { enabled: hasAccount });

  useEffect(() => {
    if (accounts.length === 0) {
      setSelectedAccountId(null);
      setExpandedAccountIds(new Set());
      return;
    }
    if (selectedAccountId != null && accounts.some((a) => a.id === selectedAccountId)) {
      return;
    }
    const next =
      syncableAccounts.find((a) => a.isDefault) ??
      syncableAccounts[0] ??
      accounts.find((a) => a.isDefault) ??
      accounts[0];
    setSelectedAccountId(next?.id ?? null);
  }, [accounts, selectedAccountId]);

  useEffect(() => {
    if (selectedAccountId == null) return;
    setExpandedAccountIds((prev) => {
      if (prev.size > 0) return prev;
      return new Set([selectedAccountId]);
    });
  }, [selectedAccountId]);

  /** Hub Simple / URL : ?uid=&accountId=&folder= → ouvrir le message. */
  const mailDeepLinkAppliedRef = useRef(false);
  useEffect(() => {
    if (mailDeepLinkAppliedRef.current) return;
    if (accounts.length === 0) return;
    const deep = readMailDeepLink();
    if (deep.uid == null) {
      mailDeepLinkAppliedRef.current = true;
      return;
    }
    mailDeepLinkAppliedRef.current = true;
    clearMailDeepLinkFromUrl();
    const accountId =
      deep.accountId != null && accounts.some((a) => a.id === deep.accountId)
        ? deep.accountId
        : (accounts.find((a) => a.isDefault)?.id ?? accounts[0]?.id ?? null);
    if (accountId != null) {
      setSelectedAccountId(accountId);
      setExpandedAccountIds((prev) => new Set(prev).add(accountId));
      setSelectedReaderAccountId(accountId);
    }
    const folder = deep.folder || "INBOX";
    setSelectedFolder(folder);
    setSelectedUid(deep.uid);
  }, [accounts]);

  const navigationOpen =
    isUnifiedInboxNavFolder(selectedFolder) ||
    (selectedAccountId != null && expandedAccountIds.has(selectedAccountId));

  const foldersState = useMailFolders(active?.id, {
    skipImap: active != null && pausedImapAccountIds.has(active.id),
  });
  const { getFolders: getAccountFolders, isFoldersLoading: isAccountFoldersLoading, refreshAllAccounts, adjustAccountFolderUnread } =
    useExpandedAccountsFolders(
      expandedAccountIds,
      active?.id,
      foldersState.folders,
      foldersState.loading,
      foldersState.adjustFolderUnread,
      { pausedAccountIds: pausedImapAccountIds }
    );
  useMailBootstrap(active?.id, foldersState.folders, {
    foldersReady:
      !foldersState.loading &&
      foldersState.folders.length > 1 &&
      !(active != null && pausedImapAccountIds.has(active.id)),
  });
  useAutoFolderHeadersPrefetch(
    active != null && pausedImapAccountIds.has(active.id) ? undefined : active?.id,
    foldersState.folders
  );
  useMailWorkerPresence(
    active != null && pausedImapAccountIds.has(active.id) ? undefined : active?.id,
    selectedFolder
  );
  const activeAccountSettings = useMemo(
    () => (active ? readAccountSettings(active.id) : undefined),
    [active?.id, settingsVersion]
  );

  const activeAccountLabel = useMemo(
    () => (active ? getAccountDisplayLabel(active) : ""),
    [active, settingsVersion]
  );

  const isOutboxView = isOutboxNavFolder(selectedFolder);
  const isSnoozedVirtualView = isSnoozedNavFolder(selectedFolder);
  const isUnifiedView = isUnifiedInboxNavFolder(selectedFolder);
  const snoozeHoldImapFolder = useMemo(
    () => resolveSnoozedImapFolder(foldersState.folders, activeAccountSettings),
    [activeAccountSettings, foldersState.folders]
  );
  const isSnoozedImapFolderView = useMemo(
    () =>
      !isSnoozedVirtualView &&
      snoozeHoldImapFolder != null &&
      selectedFolder === snoozeHoldImapFolder,
    [isSnoozedVirtualView, selectedFolder, snoozeHoldImapFolder]
  );
  const isSnoozedView = isSnoozedVirtualView || isSnoozedImapFolderView;
  const isVirtualListView = isOutboxView || isUnifiedView || isSnoozedView;

  const unifiedInbox = useUnifiedInbox(syncableAccounts, isUnifiedView);
  useMultAccountInboxPrefetch(syncableAccounts, { enabled: hasAccount });

  const isSearchActive = searchInput.trim().length > 0;
  const prevSearchActiveRef = useRef(isSearchActive);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearchQuery(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const wasActive = prevSearchActiveRef.current;
    prevSearchActiveRef.current = isSearchActive;
    if (wasActive && !isSearchActive) {
      setSelectedUid(null);
    }
  }, [isSearchActive]);

  const folderBadgeRefreshRef = useRef<() => void>(() => {});

  const mutationInFlight = useMailMutationActive();

  const inbox = useMailInboxSync(
    isVirtualListView ? undefined : imapAccountId,
    isVirtualListView ? "INBOX" : selectedFolder,
    isVirtualListView ? "" : searchQuery,
    isVirtualListView ? "" : searchInput,
    !isVirtualListView && listFilter.unreadOnly,
    {
      folders: foldersState.folders,
      enabled:
        !isVirtualListView &&
        !isSearchActive &&
        hasAccount &&
        imapAccountId != null,
      pauseImapWhileReading:
        !isVirtualListView &&
        (selectedUid != null || composeOpen || draftSavePending),
      pauseImapFully: !isVirtualListView && (draftSavePending || mutationInFlight),
      onFolderBadgesRefresh: () => folderBadgeRefreshRef.current(),
    }
  );

  useMultAccountInboxBackgroundSync(syncableAccounts, {
    enabled: hasAccount && syncableAccounts.length > 0,
    skipAccountId: imapAccountId,
  });

  const boot = useMailAppBoot({
    accountsLoading,
    accountsError,
    hasAccount,
    accountsHasList: accounts.length > 0 || (!accountsLoading && accountsError == null),
    accountId: active?.id,
    foldersLoading: foldersState.loading,
    foldersHasList: foldersState.folders.length > 0,
    inboxListSettled: inbox.listSettled === true,
  });

  const mailSnooze = useMailSnooze(imapAccountId);
  const snoozedRows = useSnoozedListRows(
    imapAccountId,
    mailSnooze.snoozes,
    { holdFolder: snoozeHoldImapFolder, enabled: imapAccountId != null }
  );

  const mailRefresh = useMailRefresh(
    isVirtualListView ? undefined : imapAccountId,
    selectedFolder,
    inbox,
    foldersState
  );
  const {
    refreshFolders,
    refreshInboxList,
    invalidateFolders,
    pullMailbox,
    recoverAfterMutationError,
    adjustFolderUnread,
  } = mailRefresh;
  folderBadgeRefreshRef.current = () => refreshFolders(false);

  const messageLabelsFolder = useMemo(() => {
    if (isOutboxView) return "INBOX";
    if (isSnoozedView && selectedUid != null) {
      return mailSnooze.snoozes.find((s) => s.uid === selectedUid)?.folder ?? "INBOX";
    }
    if (isSnoozedVirtualView) return "INBOX";
    return selectedFolder;
  }, [
    isOutboxView,
    isSnoozedView,
    isSnoozedVirtualView,
    mailSnooze.snoozes,
    selectedFolder,
    selectedUid,
  ]);

  const messageLabels = useMessageLabels(
    isOutboxView ? undefined : active?.id,
    messageLabelsFolder
  );

  const handleDelivered = useCallback(
    (sentFolder?: string | null) => {
      if (!active) return;
      const folder =
        sentFolder?.trim() ||
        resolveSentImapFolder(foldersState.folders, activeAccountSettings);
      if (!folder) return;
      clearBodiesWarming(active.id, folder);
      invalidateFolders({
        accountId: active.id,
        folders: [folder],
        reloadListIfViewing: true,
        forceList: false,
      });
      void syncFolderHeaders(active.id, folder);
    },
    [active, activeAccountSettings, foldersState.folders, invalidateFolders]
  );

  const sendQueue = useMailSendQueue({
    accountId: active?.id,
    onDelivered: handleDelivered,
    resolveSentFolder: (accountId) => {
      const folders =
        getAccountFolders?.(accountId) ??
        (accountId === active?.id ? foldersState.folders : []);
      const settings = readAccountSettings(accountId);
      return resolveSentImapFolder(folders, settings) ?? undefined;
    },
  });

  const scheduledSends = useMailScheduledSends({
    accountId: active?.id,
    enabled: isOutboxView,
    watchDue: active?.id != null,
  });

  const nextDueAtMs = useMemo(() => {
    const now = Date.now();
    let hasOverdue = false;
    const times: number[] = [];
    for (const entry of mailSnooze.snoozes) {
      const t = new Date(entry.until).getTime();
      if (!Number.isFinite(t)) continue;
      if (t <= now) hasOverdue = true;
      else times.push(t);
    }
    for (const entry of scheduledSends.entries) {
      if (entry.status !== "pending") continue;
      const t = new Date(entry.sendAt).getTime();
      if (!Number.isFinite(t)) continue;
      if (t <= now) hasOverdue = true;
      else times.push(t);
    }
    if (hasOverdue) return now + 5_000;
    return times.length > 0 ? Math.min(...times) : null;
  }, [mailSnooze.snoozes, scheduledSends.entries]);

  const syncableAccountIds = useMemo(
    () => syncableAccounts.map((account) => account.id),
    [syncableAccounts]
  );

  useMailDueImapRefresh({
    accountId: active?.id,
    holdFolder: snoozeHoldImapFolder,
    nextDueAtMs,
    accountIds: syncableAccountIds,
    enabled: hasAccount,
  });

  useEffect(() => {
    if (active?.id == null) return;

    const handler = (event: Event) => {
      const detail = (event as CustomEvent<MailSnoozeWokeDetail>).detail;
      if (!detail || detail.accountId !== active.id || detail.entries.length === 0) return;

      const localOnly = detail.entries.every((entry) => !entry.holdFolder?.trim());
      const folders = new Set<string>();
      for (const entry of detail.entries) {
        if (entry.folder.trim()) folders.add(entry.folder.trim());
        const hold = entry.holdFolder?.trim();
        if (hold) folders.add(hold);
      }
      if (!localOnly && snoozeHoldImapFolder?.trim()) {
        folders.add(snoozeHoldImapFolder.trim());
      }

      // Gmail overlay : ne pas vider INBOX (sinon re-prep ×N). Autres : invalidate hold+origine.
      if (!localOnly) {
        invalidateFolders({
          accountId: active.id,
          folders: [...folders],
          reloadListIfViewing: true,
          forceList: false,
        });
      }

      const currentFolder = selectedFolderRef.current;
      if (
        detail.entries.some(
          (entry) => entry.folder === currentFolder || entry.folder.toUpperCase() === "INBOX"
        )
      ) {
        refreshInboxList({ force: false, silent: true, background: true });
      }
    };

    window.addEventListener(MAIL_SNOOZE_WOKE_EVENT, handler);
    return () => window.removeEventListener(MAIL_SNOOZE_WOKE_EVENT, handler);
  }, [active?.id, invalidateFolders, refreshInboxList, snoozeHoldImapFolder]);

  useEffect(() => {
    mailSnooze.setOnWake((woke: MailSnoozeWoke[]) => {
      if (woke.length === 0 || active?.id == null) return;
      playMailNotificationSound();

      const viewingFolder = selectedFolderRef.current;
      for (const item of woke) {
        if (item.folder === viewingFolder && !isSnoozedNavFolder(viewingFolder)) {
          inbox.applyLocalSeenFlags([item.uid], false);
        }
        const hold = item.holdFolder?.trim();
        // Overlay Gmail : le mail n’a jamais quitté INBOX — ne pas clearFolderSnapshot.
        if (hold) {
          clearFolderSnapshot(active.id, hold);
          clearFolderSnapshot(active.id, item.folder);
        }
      }

      const needsImapRestore = woke.some((item) => Boolean(item.holdFolder?.trim()));
      if (needsImapRestore) {
        void refreshFolders(true);
      }
      refreshInboxList({ silent: true, background: true });
    });
    return () => mailSnooze.setOnWake(null);
  }, [active?.id, inbox, mailSnooze, refreshFolders, refreshInboxList]);

  const [scheduledCacheRevision, setScheduledCacheRevision] = useState(0);
  const [snoozeCacheRevision, setSnoozeCacheRevision] = useState(0);

  useEffect(() => {
    return subscribeScheduledSendsCacheChanged(() => {
      setScheduledCacheRevision((value) => value + 1);
    });
  }, []);

  useEffect(() => {
    return subscribeSnoozeCacheChanged(() => {
      setSnoozeCacheRevision((value) => value + 1);
    });
  }, []);

  const getAccountSnoozeCount = useCallback(
    (accountId: number) => {
      void snoozeCacheRevision;
      if (accountId === active?.id) {
        return Math.max(mailSnooze.snoozedCount, snoozedRows.messages.length);
      }
      return getSnoozeCachedCount(accountId);
    },
    [
      active?.id,
      mailSnooze.snoozedCount,
      snoozeCacheRevision,
      snoozedRows.holdFolderCount,
      snoozedRows.messages.length,
    ]
  );

  const reloadSnoozeList = mailSnooze.reload;

  useEffect(() => {
    if (!isSnoozedView || active?.id == null) return;
    void reloadSnoozeList({ silent: true });
  }, [active?.id, isSnoozedView, reloadSnoozeList]);

  const reloadScheduledSends = scheduledSends.reload;

  useEffect(() => {
    if (!isOutboxView || active?.id == null) return;
    if (schedulePendingAccountId === active.id) return;
    const cached = getScheduledSendsCache(active.id);
    if (cached?.some((entry) => entry.id < 1)) return;
    void reloadScheduledSends();
  }, [active?.id, isOutboxView, reloadScheduledSends, schedulePendingAccountId]);

  const getAccountOutboxState = useCallback(
    (accountId: number): AccountOutboxState => {
      void scheduledCacheRevision;
      void schedulePendingAccountId;
      const schedulingPending = accountSchedulingPending(
        accountId,
        schedulePendingAccountId
      );
      const busy = accountOutboxSendingBusy(accountId, {
        activeAccountId: active?.id,
        schedulePendingAccountId,
        activeScheduledSendingCount: scheduledSends.sendingCount,
        activeQueueSendingCount: sendQueue.sendingCount,
      });
      const base = resolveAccountOutboxState(
        accountId,
        active?.id,
        sendQueue.queueCount,
        sendQueue.sendingCount +
          (accountId === active?.id ? scheduledSends.sendingCount : 0),
        sendQueue.failedCount
      );
      return {
        ...base,
        sendingCount: Math.max(base.sendingCount, busy ? 1 : 0),
        scheduledCount: getScheduledSendsCachedCount(accountId),
        schedulingPending,
      };
    },
    [
      active?.id,
      schedulePendingAccountId,
      scheduledCacheRevision,
      scheduledSends.sendingCount,
      sendQueue.failedCount,
      sendQueue.queueCount,
      sendQueue.sendingCount,
    ]
  );

  const outboxMessages = useMemo(() => {
    if (!active) return [];
    return mergeOutboxSummaries(
      scheduledSends.toSummaries(active.email),
      sendQueue.toSummaries(active.email)
    );
  }, [active, scheduledSends, sendQueue]);

  const imapUnreadFilter = listFilter.unreadOnly;
  const clientFilterActive = inboxClientFilterIsActive(listFilter);

  const selectedFolderUnread = useMemo(() => {
    if (isOutboxView) return 0;
    const match = flattenFoldersForPicker(foldersState.folders).find(
      (f) => f.name === selectedFolder
    );
    return match?.unread ?? 0;
  }, [foldersState.folders, isOutboxView, selectedFolder]);

  const isDraftsFolder = useMemo(() => {
    if (isVirtualListView) return false;
    const folderMeta = flattenFoldersForPicker(foldersState.folders).find(
      (f) => f.name === selectedFolder
    );
    const label = folderMeta?.label ?? selectedFolder;
    if (navFolderSlot(selectedFolder, label) === "drafts") return true;
    return folderMeta != null && isDraftsNavFolder(folderMeta.name, folderMeta.label);
  }, [foldersState.folders, isOutboxView, selectedFolder]);

  const isSentFolder = useMemo(() => {
    if (isVirtualListView) return false;
    const folderMeta = flattenFoldersForPicker(foldersState.folders).find(
      (f) => f.name === selectedFolder
    );
    const label = folderMeta?.label ?? selectedFolder;
    return navFolderSlot(selectedFolder, label) === "sent";
  }, [foldersState.folders, isVirtualListView, selectedFolder]);

  const refreshDraftFolder = useCallback(
    async ({ accountId, draftFolder, afterSave = false }: DraftFolderRefreshSpec) => {
      clearFolderSnapshot(accountId, draftFolder);
      const selected = selectedFolderRef.current;
      const sameFolder =
        selected === draftFolder ||
        selected.localeCompare(draftFolder, undefined, { sensitivity: "accent" }) === 0;
      // Vue Brouillons ouverte mais save dans un autre chemin IMAP (Drafts vs INBOX.Drafts).
      const viewingDraftsList = isDraftsFolder;

      if (sameFolder || viewingDraftsList) {
        if (afterSave) {
          await syncFolderHeaders(accountId, draftFolder);
          if (!sameFolder && selected) {
            clearFolderSnapshot(accountId, selected);
            await syncFolderHeaders(accountId, selected);
          }
          await inbox.reload(true, { background: true, silent: true });
        } else {
          await inbox.reload(false, { background: true, silent: true });
        }
      } else if (afterSave) {
        void syncFolderHeaders(accountId, draftFolder);
      }

      void foldersState.reload(true);
    },
    [foldersState, inbox, isDraftsFolder]
  );

  const listDisplay = useInboxListDisplay({
    isOutboxView,
    isDraftsFolder,
    accountId: active?.id,
    selectedFolder,
    listFilter,
    inboxMessages: inbox.messages,
    messageLabels,
    outboxMessages,
    pendingDraftSaves,
    mailSnooze,
    folders: foldersState.folders,
    accountSettings: activeAccountSettings,
  });
  const { labeledMessages, displayMessages: folderDisplayMessages, clientFilter } = listDisplay;

  const folderDisplayMessagesResolved = useMemo(() => {
    if (isSnoozedView) {
      const source =
        snoozedRows.messages.length > 0
          ? snoozedRows.messages
          : mailSnooze.snoozedMessages;
      return applyInboxListFilter(source, clientFilter);
    }
    return folderDisplayMessages;
  }, [
    clientFilter,
    folderDisplayMessages,
    isSnoozedView,
    mailSnooze.snoozedMessages,
    snoozedRows.messages,
  ]);

  const applySearchLabels = useCallback(
    (messages: MailMessageSummary[]) => messageLabels.applyToMessages(messages),
    [messageLabels]
  );

  const searchDisplay = useMailSearchDisplay({
    accountId: active?.id,
    folder: selectedFolder,
    isOutboxView: isVirtualListView,
    searchInput,
    searchQuery,
    serverSearchQuery: inbox.searchResultQuery,
    serverMessages: labeledMessages,
    serverSyncing: inbox.syncing,
    applyLabels: applySearchLabels,
  });

  const accountLabelById = useMemo(() => {
    const map = new Map<number, string>();
    for (const acc of orderedAccounts) {
      map.set(acc.id, getAccountDisplayLabel(acc));
    }
    return map;
  }, [orderedAccounts, settingsVersion]);

  const [folderBadgeRevision, setFolderBadgeRevision] = useState(0);
  const [unreadHoldRevision, setUnreadHoldRevision] = useState(0);

  useEffect(() => {
    return subscribeFolderSnapshotsChanged(() => {
      setFolderBadgeRevision((v) => v + 1);
    });
  }, []);

  useEffect(() => {
    return subscribeUnreadHold(() => {
      setUnreadHoldRevision((v) => v + 1);
    });
  }, []);

  const unifiedUnreadCount = useMemo(() => {
    void folderBadgeRevision;
    void unreadHoldRevision;
    const raw = orderedAccounts.reduce((sum, acc) => {
      const folders = mergeFoldersWithSnapshotUnread(acc.id, getAccountFolders(acc.id));
      return sum + sumInboxUnreadFromFolders(folders);
    }, 0);
    return Math.max(0, raw - getTotalUnreadHold());
  }, [
    folderBadgeRevision,
    getAccountFolders,
    orderedAccounts,
    unreadHoldRevision,
  ]);

  const unifiedMessagesWithAttachments = useMultiAccountAttachmentFlags(
    isUnifiedView ? unifiedInbox.messages : []
  );

  const unifiedClientFilter = useMemo(
    () => splitInboxListFilter(listFilter).clientFilter,
    [listFilter]
  );

  const unifiedDisplayMessages = useMemo(() => {
    if (!isUnifiedView) return [];
    let rows = unifiedMessagesWithAttachments;
    if (listFilter.unreadOnly) {
      rows = rows.filter((m) => !m.seen);
    }
    return applyInboxListFilter(rows, unifiedClientFilter);
  }, [
    isUnifiedView,
    listFilter.unreadOnly,
    unifiedClientFilter,
    unifiedMessagesWithAttachments,
  ]);

  const listInboxApi = useMemo(
    () =>
      isUnifiedView
        ? {
            removeMessages: unifiedInbox.removeMessages,
            markMessageSeen: unifiedInbox.markMessageSeen,
            markMessageUnread: unifiedInbox.markMessageUnread,
            setMessagesSeen: unifiedInbox.setMessagesSeen,
            markMessageFlagged: () => {},
          }
        : inbox,
    [inbox, isUnifiedView, unifiedInbox]
  );

  const displayMessages = isUnifiedView
    ? unifiedDisplayMessages
    : searchDisplay.isSearchView
      ? searchDisplay.messages
      : folderDisplayMessagesResolved;

  const readerImapFolder = useMemo(() => {
    if (isSnoozedView && selectedUid != null) {
      const row =
        folderDisplayMessagesResolved.find((m) => m.uid === selectedUid) ??
        snoozedRows.messages.find((m) => m.uid === selectedUid);
      if (row?.imapFolder) return row.imapFolder;
      const entry = mailSnooze.snoozes.find((s) => s.uid === selectedUid);
      return entry?.holdFolder?.trim() || entry?.folder || snoozeHoldImapFolder || "INBOX";
    }
    if (isUnifiedView && selectedUid != null) {
      const readerAccountHint =
        selectedReaderAccountId ??
        displayMessages.find((m) => m.uid === selectedUid && m.accountId != null)?.accountId ??
        unifiedInbox.messages.find((m) => m.uid === selectedUid && m.accountId != null)?.accountId ??
        null;
      if (readerAccountHint != null) {
        const row = findMessageRowForReader(
          selectedUid,
          {
            displayMessages,
            labeledMessages,
            inboxMessages: unifiedInbox.messages,
          },
          readerAccountHint
        );
        if (row?.imapFolder) return row.imapFolder;
      }
      return "INBOX";
    }
    return isVirtualListView ? "INBOX" : selectedFolder;
  }, [
    displayMessages,
    folderDisplayMessagesResolved,
    isSnoozedView,
    isUnifiedView,
    isVirtualListView,
    labeledMessages,
    mailSnooze.snoozes,
    selectedFolder,
    selectedReaderAccountId,
    selectedUid,
    snoozeHoldImapFolder,
    snoozedRows.messages,
    unifiedInbox.messages,
  ]);

  const resolveMessageFolder = useCallback(
    (uid: number, accountId?: number) => {
      const readerAccountId =
        accountId ?? (isUnifiedView ? selectedReaderAccountId : undefined);
      const inboxMessagesForLookup = isUnifiedView ? unifiedInbox.messages : inbox.messages;
      const row =
        findMessageRowForReader(
          uid,
          { displayMessages, labeledMessages, inboxMessages: inboxMessagesForLookup },
          readerAccountId
        ) ?? snoozedRows.messages.find((m) => m.uid === uid);
      if (row?.imapFolder) return row.imapFolder;
      const entry = mailSnooze.snoozes.find((s) => s.uid === uid);
      if (entry?.folder) return entry.folder;
      if (isUnifiedView) return "INBOX";
      return selectedFolder;
    },
    [
      displayMessages,
      inbox.messages,
      isUnifiedView,
      labeledMessages,
      mailSnooze.snoozes,
      selectedFolder,
      selectedReaderAccountId,
      snoozedRows.messages,
      unifiedInbox.messages,
    ]
  );

  const listBodyFolderByUid = useMemo(() => {
    if (!isSnoozedView) return undefined;
    return snoozedRows.bodyFolderByUid;
  }, [isSnoozedView, snoozedRows.bodyFolderByUid]);

  const listBodyUidByUid = useMemo(() => {
    if (!isSnoozedView) return undefined;
    return snoozedRows.bodyUidByUid;
  }, [isSnoozedView, snoozedRows.bodyUidByUid]);

  const listSnoozeUntilByUid = useMemo(() => {
    if (!isSnoozedView) return undefined;
    return snoozedRows.snoozeUntilByUid;
  }, [isSnoozedView, snoozedRows.snoozeUntilByUid]);

  displayMessagesRef.current = displayMessages;

  invalidateFoldersRef.current = invalidateFolders;
  removeInboxMessageRef.current = inbox.removeMessage;
  onEnqueueRef.current = (payload) => sendQueue.enqueue(payload);

  const listMismatchRetryRef = useRef<string | null>(null);

  useEffect(() => {
    listMismatchRetryRef.current = null;
  }, [active?.id, selectedFolder]);

  useEffect(() => {
    if (isOutboxView || isUnifiedView || active?.id == null || inbox.syncing || isSearchActive) return;
    if (isDraftsFolder || isSentFolder || draftSavePending) return;
    if (searchQuery.trim().length > 0) return;

    const onlyClientFilters = inboxClientFilterIsActive(listFilter);
    if (onlyClientFilters) return;

    const key = `${active.id}:${selectedFolder}`;
    const listCount = inbox.messages.length;
    const visibleUnread = inbox.messages.filter((m) => !m.seen).length;
    const snapshotUnread = countUnreadInFolderSnapshot(active.id, selectedFolder);

    const mismatch =
      selectedFolderUnread > snapshotUnread ||
      (selectedFolderUnread > 0 && listCount === 0) ||
      (selectedFolderUnread > 0 && visibleUnread === 0 && !listFilter.unreadOnly);

    if (!mismatch) {
      listMismatchRetryRef.current = null;
      return;
    }

    if (listMismatchRetryRef.current === key) return;
    listMismatchRetryRef.current = key;

    void syncFolderHeaders(active.id, selectedFolder).finally(() => {
      refreshInboxList({
        force: true,
        silent: true,
        background: listCount > 0,
      });
      refreshFolders(true);
    });
  }, [
    active?.id,
    selectedFolder,
    selectedFolderUnread,
    inbox.messages,
    inbox.syncing,
    isOutboxView,
    isUnifiedView,
    isDraftsFolder,
    isSentFolder,
    isSearchActive,
    searchQuery,
    listFilter,
    refreshFolders,
    refreshInboxList,
    draftSavePending,
  ]);

  const selectedMessagePreview = useMemo(() => {
    if (isOutboxView || selectedUid == null) return null;
    if (isUnifiedView) {
      const readerAccountHint =
        selectedReaderAccountId ??
        displayMessages.find((m) => m.uid === selectedUid && m.accountId != null)?.accountId ??
        unifiedInbox.messages.find((m) => m.uid === selectedUid && m.accountId != null)?.accountId ??
        null;
      if (readerAccountHint == null) return null;
      return findMessageRowForReader(
        selectedUid,
        {
          displayMessages,
          labeledMessages,
          inboxMessages: unifiedInbox.messages,
        },
        readerAccountHint
      );
    }
    return findMessageRowForReader(selectedUid, {
      displayMessages,
      labeledMessages,
    });
  }, [
    displayMessages,
    isOutboxView,
    isUnifiedView,
    labeledMessages,
    selectedReaderAccountId,
    selectedUid,
    unifiedInbox.messages,
  ]);

  const readerAccountId = useMemo(() => {
    if (isOutboxView) return undefined;
    if (isUnifiedView) {
      if (selectedReaderAccountId != null) return selectedReaderAccountId;
      if (selectedUid != null) {
        const row =
          displayMessages.find(
            (m) => m.uid === selectedUid && m.accountId != null
          ) ?? unifiedInbox.messages.find(
            (m) => m.uid === selectedUid && m.accountId != null
          );
        return row?.accountId ?? undefined;
      }
      return undefined;
    }
    return active?.id;
  }, [
    active?.id,
    displayMessages,
    isOutboxView,
    isUnifiedView,
    selectedReaderAccountId,
    selectedUid,
    unifiedInbox.messages,
  ]);

  const selectedReaderFlags = useMemo(() => {
    if (isOutboxView || selectedUid == null) {
      return { flagged: false, favorite: false };
    }
    const row = findMessageRowForReader(
      selectedUid,
      {
        displayMessages,
        labeledMessages,
        inboxMessages: isUnifiedView ? unifiedInbox.messages : inbox.messages,
      },
      isUnifiedView ? selectedReaderAccountId : undefined
    );
    const label = messageLabels.labels.get(selectedUid);
    return {
      flagged: row?.flagged ?? false,
      favorite: label?.favorite ?? row?.favorite ?? false,
    };
  }, [
    displayMessages,
    inbox.messages,
    isOutboxView,
    isUnifiedView,
    labeledMessages,
    messageLabels.labels,
    selectedReaderAccountId,
    selectedUid,
    unifiedInbox.messages,
  ]);

  const readerBodyUid = useMemo(() => {
    if (isOutboxView || selectedUid == null) return selectedUid;
    const readerAccountHint = isUnifiedView
      ? selectedReaderAccountId ??
        displayMessages.find((m) => m.uid === selectedUid && m.accountId != null)?.accountId ??
        unifiedInbox.messages.find((m) => m.uid === selectedUid && m.accountId != null)?.accountId ??
        null
      : undefined;
    const row = findMessageRowForReader(
      selectedUid,
      {
        displayMessages,
        labeledMessages,
        inboxMessages: isUnifiedView ? unifiedInbox.messages : inbox.messages,
      },
      readerAccountHint
    );
    return row?.imapBodyUid ?? selectedUid;
  }, [
    displayMessages,
    inbox.messages,
    isOutboxView,
    isUnifiedView,
    labeledMessages,
    selectedReaderAccountId,
    selectedUid,
    unifiedInbox.messages,
  ]);

  const readerSelectionKey =
    isOutboxView || selectedUid == null
      ? null
      : isUnifiedView && selectedReaderAccountId != null
        ? `${selectedReaderAccountId}:${selectedUid}`
        : String(selectedUid);

  const pinnedReaderTargetRef = useRef<{
    key: string | null;
    bodyUid: number | null;
    imapFolder: string;
    accountId: number | undefined;
  }>({
    key: null,
    bodyUid: null,
    imapFolder: "INBOX",
    accountId: undefined,
  });

  if (readerSelectionKey !== pinnedReaderTargetRef.current.key) {
    pinnedReaderTargetRef.current = {
      key: readerSelectionKey,
      bodyUid: readerBodyUid,
      imapFolder: readerImapFolder,
      accountId: readerAccountId,
    };
  }

  const pinnedReaderBodyUid =
    pinnedReaderTargetRef.current.key === readerSelectionKey
      ? pinnedReaderTargetRef.current.bodyUid
      : readerBodyUid;
  const pinnedReaderImapFolder =
    pinnedReaderTargetRef.current.key === readerSelectionKey
      ? pinnedReaderTargetRef.current.imapFolder
      : readerImapFolder;
  const pinnedReaderAccountId =
    pinnedReaderTargetRef.current.key === readerSelectionKey
      ? pinnedReaderTargetRef.current.accountId
      : readerAccountId;

  const reader = useMailMessage(
    isOutboxView ? undefined : pinnedReaderAccountId,
    isOutboxView ? null : pinnedReaderBodyUid,
    pinnedReaderImapFolder,
    selectedMessagePreview
  );

  const compose = useComposeActions({
    accounts: orderedAccounts,
    account: active,
    selectedFolder,
    folders: foldersState.folders,
    getAccountFolders,
    isOutboxView,
    displayMessages: displayMessagesRef.current,
    readerMessage: reader.message,
    refreshDraftFolder,
    removeInboxMessage: (uid) => removeInboxMessageRef.current(uid),
    resolveMessageFolder,
    onEnqueue: (payload) => onEnqueueRef.current(payload),
    onScheduleBegin: ({ accountId, sendAt, subject }) => {
      setSchedulePendingAccountId(accountId);
      setExpandedAccountIds((prev) => new Set(prev).add(accountId));
      upsertScheduledSendCache(accountId, {
        id: -1,
        accountId,
        sendAt,
        status: "pending",
        subject: subject.trim() || "(Sans objet)",
      });
      if (active?.id === accountId) {
        scheduledSends.upsert({
          id: -1,
          accountId,
          sendAt,
          status: "pending",
          subject: subject.trim() || "(Sans objet)",
        });
        setSearchInput("");
        setSelectedFolder(AURELIA_OUTBOX_FOLDER);
        setSelectedUid(null);
      }
    },
    onScheduleEnd: () => setSchedulePendingAccountId(null),
    onScheduled: (entry) => {
      if (!entry) return;
      setSchedulePendingAccountId(null);
      removeScheduledSendPlaceholders(entry.accountId);
      upsertScheduledSendCache(entry.accountId, entry);
      if (active?.id === entry.accountId) {
        scheduledSends.upsert(entry);
        setSearchInput("");
        setSelectedFolder(AURELIA_OUTBOX_FOLDER);
        const summaries = scheduledSendsToSummaries(
          getScheduledSendsCache(entry.accountId) ?? [entry],
          active.email
        );
        const row = summaries.find((m) => m.scheduledSendId === entry.id);
        setSelectedUid(row?.uid ?? null);
      }
    },
    onSessionChange: setComposeOpen,
    onSavingDraftsChange: (pending, accountId) => {
      setDraftSavePending(pending);
      setDraftSavePendingAccountId(pending ? (accountId ?? null) : null);
    },
    onPendingDraftSavesChange: setPendingDraftSaves,
    inlineReplyEnabled: true,
    readerAccountId: pinnedReaderAccountId,
    selectedReaderAccountId,
    isUnifiedInboxView: isUnifiedView,
    selectedListUid: selectedUid,
    resolveDraftsFolder: (accountId) => {
      const folders =
        getAccountFolders?.(accountId) ??
        (accountId === active?.id ? foldersState.folders : []);
      const settings = readAccountSettings(accountId);
      return resolveDraftsImapFolder(folders, settings);
    },
  });

  const inlineComposeSession = compose.composeSession;
  const inlineComposeOpenedRef = useRef<string | null>(null);

  useEffect(() => {
    const session = inlineComposeSession;
    if (!session || session.placement !== "inline") {
      inlineComposeOpenedRef.current = null;
      return;
    }
    const sourceUid = session.inlineReplySourceUid;
    if (sourceUid == null) return;

    if (inlineComposeOpenedRef.current !== session.id) {
      inlineComposeOpenedRef.current = session.id;
      if (selectedUid !== sourceUid) {
        setSelectedUid(sourceUid);
        if (isUnifiedView) {
          setSelectedReaderAccountId(session.accountId);
        }
      }
      return;
    }

    if (selectedUid == null || selectedUid !== sourceUid) {
      compose.closeComposeSession(session.id);
    }
  }, [
    inlineComposeSession,
    isUnifiedView,
    selectedUid,
    compose.closeComposeSession,
  ]);

  const resolveSnoozeHoldFolder = useCallback(
    () => resolveSnoozedImapFolder(foldersState.folders, activeAccountSettings),
    [activeAccountSettings, foldersState.folders]
  );

  const resolveTrashFolder = useCallback(
    (accountId: number) => {
      const folders =
        getAccountFolders?.(accountId) ??
        (accountId === active?.id ? foldersState.folders : []);
      const settings = readAccountSettings(accountId);
      return resolveTrashImapFolder(folders, settings);
    },
    [active?.id, foldersState.folders, getAccountFolders]
  );

  const bulk = useInboxBulkActions({
    account: active,
    isOutboxView,
    selectedFolder,
    selectedUid,
    setSelectedUid,
    displayMessages,
    labeledMessages,
    inboxMessages: isUnifiedView ? unifiedInbox.messages : inbox.messages,
    inbox: listInboxApi,
    messageLabels,
    mailSnooze,
    isSnoozedView,
    isDraftsFolder,
    resolveMessageFolder,
    inboxListActive: isUnifiedView || (!isOutboxView && !isSnoozedView),
    adjustFolderUnread,
    adjustAccountFolderUnread,
    invalidateFolders,
    recoverAfterMutationError,
    openDraftForEdit: compose.openDraftForEdit,
    openComposeForUid: compose.openComposeForUid,
    isUnifiedInboxView: isUnifiedView,
    selectedReaderAccountId,
    setSelectedReaderAccountId,
    resolveSnoozeHoldFolder,
    resolveTrashFolder,
    onSnoozeFolderRequired: () => {
      if (active?.id == null) return;
      setSettingsAccountId(active.id);
      setSettingsFocus("navigation");
    },
  });

  const foldersWithOutbox = foldersState.folders;

  const selectedFolderLabel = useMemo(() => {
    if (selectedFolder === AURELIA_UNIFIED_INBOX_FOLDER) {
      return UNIFIED_INBOX_LABEL;
    }
    if (
      selectedFolder === AURELIA_SNOOZED_FOLDER ||
      isSnoozedListFolder(selectedFolder, foldersWithOutbox, activeAccountSettings)
    ) {
      return SNOOZED_FOLDER_LABEL;
    }
    if (selectedFolder === AURELIA_OUTBOX_FOLDER) {
      if (active?.id == null) return SCHEDULED_OUTBOX_LABEL;
      return resolveOutboxNavLabel(getAccountOutboxState(active.id));
    }
    const match = flattenFoldersForPicker(foldersWithOutbox).find((f) => f.name === selectedFolder);
    if (
      draftSavePending &&
      active?.id != null &&
      draftSavePendingAccountId === active.id &&
      match != null &&
      isDraftsNavFolder(match.name, match.label)
    ) {
      return DRAFTS_SAVING_FOLDER_LABEL;
    }
    return match?.label ?? selectedFolder;
  }, [
    selectedFolder,
    foldersWithOutbox,
    active?.id,
    getAccountOutboxState,
    activeAccountSettings,
    draftSavePending,
    draftSavePendingAccountId,
  ]);

  const settingsAccount = useMemo(
    () => accounts.find((a) => a.id === settingsAccountId) ?? null,
    [accounts, settingsAccountId]
  );

  function openAccountSettings(accountId: number, focus: AccountSettingsFocus = "navigation") {
    setSettingsAccountId(accountId);
    setSettingsFocus(focus);
  }

  function closeAccountSettings() {
    setSettingsAccountId(null);
    setSettingsFocus("navigation");
  }

  function notifyStorageStatsRefresh() {
    notifyMailStorageStatsRefresh();
  }

  function handleSearchSubmit() {
    const q = searchInput.trim();
    if (!q || isVirtualListView) return;
    setSearchQuery(q);
  }

  async function handleAccountConnected(account?: MailAccountSummary) {
    await reload();
    setConnectModalOpen(false);
    if (account?.id != null) {
      connectBootstrapRef.current = account.id;
      setSelectedAccountId(account.id);
      setExpandedAccountIds(new Set([account.id]));
      setSelectedFolder("INBOX");
      setSelectedUid(null);
      setSearchInput("");
      setSearchQuery("");
    }
  }

  useEffect(() => {
    const bootstrapId = connectBootstrapRef.current;
    if (bootstrapId == null || active?.id !== bootstrapId) return;
    connectBootstrapRef.current = null;

    void (async () => {
      clearFolderSnapshot(bootstrapId, "INBOX");
      void foldersState.reload(true);
      void syncFolderHeaders(bootstrapId, "INBOX").then(() => {
        void warmFolderMessages(bootstrapId, "INBOX");
      });
      const foldersRes = await fetchMailFolders({ accountId: bootstrapId, refresh: true });
      const folderList =
        foldersRes.ok && foldersRes.data.folders?.length ? foldersRes.data.folders : [];
      if (folderList.length > 0) {
        const pickedSent = ensureSentFolderOverride(bootstrapId, folderList);
        const aureliaMap =
          foldersRes.ok && foldersRes.data.aureliaFolders
            ? foldersRes.data.aureliaFolders
            : null;
        if (aureliaMap && Object.keys(aureliaMap).length > 0) {
          setSettingsVersion((v) => v + 1);
          showMailToast(
            "Dossiers Aurélia prêts (Envoyés, Brouillons, En attente, Indésirables, Corbeille).",
            "info",
            6000
          );
        } else if (pickedSent) {
          setSettingsVersion((v) => v + 1);
          const label =
            flattenFoldersForPicker(folderList).find((f) => f.name === pickedSent)?.label ??
            pickedSent;
          showMailToast(`Dossier Envoyés associé à « ${label} ».`, "info");
        }
        // Pas de prefetch force multi-dossiers : sur Gmail ça relance IMAP Apache
        // pour des dizaines de labels et fige toute la suite. Les dossiers se
        // chargent à la demande (clic) via DD02 / IMAP ciblé.

        if (!hasSeenFolderImapNudge(bootstrapId)) {
          const overrides = readAccountSettings(bootstrapId).folderSlotOverrides;
          if (criticalFolderSlotsNeedAttention(folderList, overrides)) {
            markFolderImapNudgeSeen(bootstrapId);
            showMailToast(
              "Vérifiez le branchement des dossiers IMAP (Envoyés, En attente…).",
              "info",
              7000
            );
            openAccountSettings(bootstrapId, "navigation");
          }
        }
      }
    })();
  }, [active?.id, foldersState.reload]);

  function handleToggleAccount(accountId: number) {
    setExpandedAccountIds((prev) => {
      const next = new Set(prev);
      const expanding = !next.has(accountId);
      if (expanding) {
        next.add(accountId);
        void syncFolderFromServerCache(accountId, "INBOX");
      } else {
        next.delete(accountId);
      }
      return next;
    });
  }

  function handleSelectFolder(accountId: number, folder: string) {
    const isImapListFolder =
      !isOutboxNavFolder(folder) &&
      !isSnoozedNavFolder(folder) &&
      !isUnifiedInboxNavFolder(folder) &&
      !isSnoozedListFolder(folder, foldersState.folders, activeAccountSettings);

    if (isImapListFolder) {
      hydrateInboxHeadersFromSession(accountId, folder);
      void syncFolderFromServerCache(accountId, folder);
      void syncFolderHeaders(accountId, folder);
    }

    if (folder === selectedFolder && accountId === active?.id) {
      setMobileNavOpen(false);
      return;
    }
    if (accountId !== active?.id) {
      setSelectedAccountId(accountId);
    }
    setExpandedAccountIds((prev) => new Set(prev).add(accountId));
    setSelectedFolder(folder);
    setSelectedUid(null);
    setSelectedReaderAccountId(null);
    compose.clearComposeSession();
    setSearchInput("");
    setSearchQuery("");
    if (
      !isOutboxNavFolder(folder) &&
      !isSnoozedNavFolder(folder) &&
      !isUnifiedInboxNavFolder(folder) &&
      !isSnoozedListFolder(folder, foldersState.folders, activeAccountSettings)
    ) {
      setListFilter(readInboxListFilter(accountId, folder));
    } else {
      setListFilter(INBOX_FILTER_DEFAULT);
    }
    setMobileNavOpen(false);

    if (
      isOutboxNavFolder(folder) ||
      isSnoozedNavFolder(folder) ||
      isUnifiedInboxNavFolder(folder) ||
      isSnoozedListFolder(folder, foldersState.folders, activeAccountSettings)
    ) {
      return;
    }
  }

  async function refreshMailbox() {
    if (isUnifiedView) {
      await unifiedInbox.pullInboxNow();
      return;
    }
    if (isVirtualListView || active?.id == null) return;
    await pullMailbox();
  }

  function handleInboxRefresh() {
    void refreshMailbox();
  }

  const selectedOutboxRow = useMemo(() => {
    if (!isOutboxView || selectedUid == null) return null;
    return outboxMessages.find((m) => m.uid === selectedUid) ?? null;
  }, [isOutboxView, outboxMessages, selectedUid]);

  const selectedOutboxItem = useMemo(() => {
    if (!selectedOutboxRow?.outboxId) return null;
    return sendQueue.getItemById(selectedOutboxRow.outboxId);
  }, [selectedOutboxRow, sendQueue]);

  const selectedScheduledSend = useMemo(() => {
    if (!selectedOutboxRow?.scheduledSendId) return null;
    return scheduledSends.getById(selectedOutboxRow.scheduledSendId);
  }, [selectedOutboxRow, scheduledSends]);

  useEffect(() => {
    bulk.clearSelection();
    if (isVirtualListView || active?.id == null) {
      setListFilter(INBOX_FILTER_DEFAULT);
      return;
    }
    setListFilter(readInboxListFilter(active.id, selectedFolder));
  }, [active?.id, bulk.clearSelection, isVirtualListView, selectedFolder]);

  /** Important = \\Flagged IMAP — resync headers depuis IMAP (le delta n’actualise pas les flags existants). */
  useEffect(() => {
    if (!listFilter.importantOnly || isVirtualListView || imapAccountId == null) return;
    refreshInboxList({ force: true, silent: true, background: true });
  }, [imapAccountId, isVirtualListView, listFilter.importantOnly, refreshInboxList, selectedFolder]);

  async function handleDisconnectAccount(accountId: number) {
    setDisconnecting(true);
    try {
      const res = await deleteMailAccount(accountId);
      if (!res.ok) {
        window.alert(res.message);
        return;
      }

      closeAccountSettings();
      setSelectedUid(null);
      setSelectedFolder("INBOX");
      removeAccount(accountId);
      void clearAccountClientMailCache(accountId).catch(() => undefined);
      void reload();
    } finally {
      setDisconnecting(false);
    }
  }

  const bootOverlay = boot.showOverlay ? (
    <MailBootScreen
      label={boot.label}
      mode="loading"
      exiting={boot.exiting}
      optimization={boot.optimization}
      onSkipBackground={boot.skipToBackground}
    />
  ) : null;

  if (!boot.ready && !boot.exiting) {
    return bootOverlay;
  }

  if (accountsError && accounts.length === 0) {
    return (
      <>
        {bootOverlay}
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
        <MailShellHeader
          actions={
            <MailShellHeaderActions onAddAccount={() => setConnectModalOpen(true)} />
          }
        />
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-4 text-center">
          <p className="text-sm text-[var(--mail-muted)]">{accountsError}</p>
          <button
            type="button"
            className="rounded-lg bg-[var(--mail-accent)] px-4 py-2 text-sm font-medium text-white"
            onClick={() => void reload()}
          >
            Réessayer
          </button>
        </div>
        </div>
      </>
    );
  }

  if (!hasAccount || !active) {
    return (
      <>
        {bootOverlay}
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
        <MailShellHeader
          actions={
            <MailShellHeaderActions onAddAccount={() => setConnectModalOpen(true)} />
          }
        />
        <AccountConnectScreen onConnected={() => void reload()} />
        </div>
      </>
    );
  }

  const detailOpen =
    isOutboxView
      ? selectedOutboxItem != null || selectedScheduledSend != null
      : selectedUid !== null;

  const navSidebar = (
    <MailNavSidebar
      accounts={orderedAccounts}
      activeAccountId={active.id}
      expandedAccountIds={expandedAccountIds}
      selectedFolder={selectedFolder}
      getAccountFolders={getAccountFolders}
      isAccountFoldersLoading={isAccountFoldersLoading}
      onToggleAccount={handleToggleAccount}
      onSelectFolder={handleSelectFolder}
      onOpenSettings={(accountId) => openAccountSettings(accountId)}
      onOpenAccountSettings={openAccountSettings}
      onCreateFolder={(accountId) => {
        setCreateFolderAccountId(accountId);
        setCreateFolderOpen(true);
      }}
      onRenameFolder={(accountId, folder) => {
        setRenameFolderAccountId(accountId);
        setRenameFolderTarget(folder);
        setRenameFolderOpen(true);
      }}
      onDeleteFolder={(accountId, folder) => {
        setDeleteFolderAccountId(accountId);
        setDeleteFolderTarget(folder);
        setDeleteFolderError(null);
        setDeleteFolderOpen(true);
      }}
      onMoveAccount={moveAccount}
      onCompose={() => compose.openCompose("new")}
      getAccountOutboxState={getAccountOutboxState}
      getAccountSnoozeCount={getAccountSnoozeCount}
      draftSavePendingAccountId={draftSavePendingAccountId}
      unifiedUnreadCount={unifiedUnreadCount}
      settingsVersion={settingsVersion}
    />
  );

  return (
    <MailContactsProvider>
      <>
      <MailContactsCollector
        messages={labeledMessages}
        readerFrom={reader.message?.from}
        readerTo={reader.message?.to}
      />
      <MailAccountSettingsModal
        open={settingsAccount != null}
        account={settingsAccount}
        folders={
          settingsAccountId != null
            ? getAccountFolders(settingsAccountId)
            : foldersState.folders
        }
        focusSection={settingsFocus}
        disconnectBusy={disconnecting}
        onClose={closeAccountSettings}
        onSaved={(accountId, saved) => {
          // Prefs locales déjà écrites — pas de LIST IMAP forcé (timeout / UI figée).
          setSettingsVersion((v) => v + 1);
          const sentFolder = resolveSentImapFolder(getAccountFolders(accountId), saved);
          if (!sentFolder) return;
          void syncFolderHeaders(accountId, sentFolder).then(() => {
            if (selectedFolderRef.current === sentFolder) {
              refreshInboxList({ force: false, silent: true, background: true });
            }
          });
        }}
        onAccountUpdated={(accountId, patch) => {
          patchAccount(accountId, patch);
          setSettingsVersion((v) => v + 1);
        }}
        onDefaultChanged={(accountId) => {
          setSelectedAccountId(accountId);
          setExpandedAccountIds(new Set([accountId]));
        }}
        onDisconnect={(accountId) => void handleDisconnectAccount(accountId)}
        onPasswordUpdated={() => void reload()}
        onPhotoUpdated={() => setSettingsVersion((v) => v + 1)}
        onCachePurged={() => {
          setSelectedUid(null);
          notifyStorageStatsRefresh();
          void foldersState.reload(false);
          if (!isOutboxView) {
            refreshInboxList({ force: false, silent: true, background: true });
          }
        }}
        onCacheRefreshed={async (options) => {
          const force = options?.force === true;
          const ok = await foldersState.reload(force);
          if (settingsAccountId != null && settingsAccountId !== active?.id) {
            void refreshAllAccounts([settingsAccountId], force);
          }
          if (!isOutboxView && !force) {
            refreshInboxList({ force: false, silent: true, background: true });
          }
          notifyStorageStatsRefresh();
          if (force && !ok) {
            throw new Error(
              "Délai dépassé ou échec IMAP en rafraîchissant les dossiers (fréquent avec Gmail)."
            );
          }
        }}
      />

      <MailCreateFolderDialog
        open={createFolderOpen && createFolderAccountId != null}
        accountId={createFolderAccountId ?? active.id}
        folders={getAccountFolders(createFolderAccountId ?? active.id)}
        onClose={() => {
          setCreateFolderOpen(false);
          setCreateFolderAccountId(null);
        }}
        onCreated={() => {
          const targetId = createFolderAccountId ?? active.id;
          void foldersState.reload(true);
          void refreshAllAccounts([targetId], true);
          notifyStorageStatsRefresh();
        }}
      />

      <MailRenameFolderDialog
        open={renameFolderOpen && renameFolderAccountId != null && renameFolderTarget != null}
        accountId={renameFolderAccountId ?? active.id}
        folder={renameFolderTarget}
        onClose={() => {
          setRenameFolderOpen(false);
          setRenameFolderAccountId(null);
          setRenameFolderTarget(null);
        }}
        onRenamed={(folder, oldName) => {
          const targetId = renameFolderAccountId ?? active.id;
          if (
            targetId === active.id &&
            selectedFolderRef.current.toUpperCase() === oldName.toUpperCase()
          ) {
            setSelectedFolder(folder.name);
          }
          void foldersState.reload(true);
          void refreshAllAccounts([targetId], true);
          notifyStorageStatsRefresh();
        }}
      />

      <MailConfirmDialog
        open={deleteFolderOpen && deleteFolderTarget != null}
        title="Supprimer le dossier ?"
        message={
          deleteFolderError ??
          `Le dossier « ${deleteFolderTarget?.label ?? ""} » sera supprimé sur le serveur IMAP. Il doit être vide (aucun message ni sous-dossier).`
        }
        confirmLabel="Supprimer"
        busy={deleteFolderBusy}
        onCancel={() => {
          if (deleteFolderBusy) return;
          setDeleteFolderOpen(false);
          setDeleteFolderAccountId(null);
          setDeleteFolderTarget(null);
          setDeleteFolderError(null);
        }}
        onConfirm={() => {
          if (!deleteFolderTarget || deleteFolderBusy) return;
          const targetId = deleteFolderAccountId ?? active.id;
          const folderName = deleteFolderTarget.name;
          setDeleteFolderBusy(true);
          setDeleteFolderError(null);
          void (async () => {
            try {
              const res = await deleteMailFolder({ accountId: targetId, folder: folderName });
              if (!res.ok) {
                setDeleteFolderError(res.message);
                return;
              }
              if (
                targetId === active.id &&
                selectedFolderRef.current.toUpperCase() === folderName.toUpperCase()
              ) {
                setSelectedFolder("INBOX");
                setSelectedUid(null);
              }
              setDeleteFolderOpen(false);
              setDeleteFolderAccountId(null);
              setDeleteFolderTarget(null);
              void foldersState.reload(true);
              void refreshAllAccounts([targetId], true);
              notifyStorageStatsRefresh();
            } catch (err) {
              setDeleteFolderError(err instanceof Error ? err.message : "Suppression impossible.");
            } finally {
              setDeleteFolderBusy(false);
            }
          })();
        }}
      />

      <AccountConnectModal
        open={connectModalOpen}
        onClose={() => setConnectModalOpen(false)}
        onConnected={(account) => void handleAccountConnected(account)}
      />

      <FolderMobileSheet open={mobileNavOpen} onClose={() => setMobileNavOpen(false)}>
        {navSidebar}
      </FolderMobileSheet>

      <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <MailShell
        detailOpen={detailOpen}
        previewExpandReading={readingExpanded}
        onToggleReadingExpand={() => setReadingExpanded((v) => !v)}
        showReadingExpandToggle={selectedUid != null}
        freezeSplitLayout={isModalComposeSession(compose.composeSession)}
        searchQuery={isVirtualListView ? "" : searchInput}
        onSearchChange={isVirtualListView ? undefined : setSearchInput}
        onSearchSubmit={isVirtualListView ? undefined : handleSearchSubmit}
        searchDisabled={isVirtualListView}
        searchBusy={!isVirtualListView && searchDisplay.imapSearchLoading}
        readingToolbar={
          detailOpen ? (
            <MessageReaderBackBar
              onBack={() => {
                setSelectedUid(null);
                setSelectedReaderAccountId(null);
              }}
            />
          ) : null
        }
        headerActions={
          <MailShellHeaderActions
            onAddAccount={() => setConnectModalOpen(true)}
            onOpenAddressBook={() => setAddressBookOpen(true)}
          />
        }
        sidebar={navSidebar}
        list={
          <InboxList
            navigationOpen={navigationOpen}
            folderLabel={selectedFolderLabel}
            accountEmail={isUnifiedView ? undefined : activeAccountLabel}
            listSubtitle={
              isUnifiedView
                ? `${orderedAccounts.length} compte${orderedAccounts.length > 1 ? "s" : ""}`
                : undefined
            }
            accountId={isUnifiedView ? undefined : active.id}
            folder={selectedFolder}
            messages={displayMessages}
            total={
              isUnifiedView
                ? displayMessages.length
                : isSnoozedView || isVirtualListView
                  ? displayMessages.length
                  : imapUnreadFilter
                    ? displayMessages.length
                    : inbox.total
            }
            syncing={
              isUnifiedView
                ? unifiedInbox.syncing
                : !isVirtualListView && (inbox.syncing || inbox.loadingInitial)
            }
            loadingInitial={
              isUnifiedView
                ? unifiedInbox.loadingInitial
                : !isVirtualListView && inbox.loadingInitial
            }
            loadingMore={!isVirtualListView ? inbox.loadingMore && !imapUnreadFilter : false}
            hasMore={
              !isVirtualListView && !searchDisplay.isSearchView
                ? inbox.hasMore && !imapUnreadFilter
                : false
            }
            error={isUnifiedView ? unifiedInbox.error : isVirtualListView ? null : inbox.error}
            fromCache={isUnifiedView ? unifiedInbox.fromCache : !isVirtualListView ? inbox.fromCache : false}
            selectedUid={selectedUid}
            contextMenuUid={bulk.contextMenu?.uid ?? null}
            isRowSelected={isUnifiedView ? bulk.isRowSelected : undefined}
            isRowChecked={isUnifiedView ? bulk.isRowChecked : undefined}
            isContextMenuRow={isUnifiedView ? bulk.isContextMenuRow : undefined}
            accountLabelForMessage={
              isUnifiedView
                ? (msg) =>
                    msg.accountId != null ? accountLabelById.get(msg.accountId) : undefined
                : undefined
            }
            accountIdForMessage={isUnifiedView ? (msg) => msg.accountId : undefined}
            searchQuery={isVirtualListView ? "" : searchDisplay.activeQuery}
            searching={searchDisplay.isSearchView}
            searchPending={searchDisplay.searchDebouncePending}
            searchImapLoading={searchDisplay.imapSearchLoading}
            searchLocalCount={searchDisplay.localMatchCount}
            searchShowEmptyResults={searchDisplay.showEmptyResults}
            searchCommittedQuery={searchDisplay.committedQuery}
            onOpenNav={() => setMobileNavOpen(true)}
            onRefresh={handleInboxRefresh}
            onLoadMore={() => void inbox.loadMore()}
            onSelectMessage={bulk.handleSelectMessage}
            onToggleCheck={bulk.handleToggleCheck}
            onContextMenu={bulk.handleRowContextMenu}
            listFilter={listFilter}
            hiddenByFilterCount={
              clientFilterActive
                ? isUnifiedView
                  ? Math.max(0, unifiedMessagesWithAttachments.length - displayMessages.length)
                  : !isVirtualListView
                    ? Math.max(0, labeledMessages.length - displayMessages.length)
                    : 0
                : 0
            }
            onListFilterChange={
              isVirtualListView && !isUnifiedView
                ? undefined
                : (patch) =>
                    setListFilter((prev) => {
                      const next = { ...prev, ...patch };
                      if (!isUnifiedView && active?.id != null) {
                        writeInboxListFilter(active.id, selectedFolder, next);
                      }
                      return next;
                    })
            }
            onBulkAction={bulk.handleBulkAction}
            onClearSelection={bulk.clearSelection}
            isDraftsFolder={isDraftsFolder}
            isSentFolder={isSentFolder}
            isOutboxFolder={isOutboxView}
            checkedUids={bulk.checkedUids}
            selectionCount={bulk.selectionCount}
            hideBulkMove={isUnifiedView}
            exitingUids={bulk.exitingUids}
            showCheckboxes={!isOutboxView}
            requireBodyReady={false}
            bodyFolderByUid={listBodyFolderByUid}
            bodyUidByUid={listBodyUidByUid}
            snoozeUntilByUid={listSnoozeUntilByUid}
            onOpenFolderStorage={
              !isVirtualListView && !isUnifiedView && active?.id != null && !isDraftsFolder
                ? () => setFolderStorageOpen(true)
                : undefined
            }
            folderStorageActive={
              active?.id != null
                ? isFolderStorageSyncActive(active.id, selectedFolder)
                : false
            }
          />
        }
        reading={
          isOutboxView && selectedScheduledSend ? (
            <ScheduledSendReader
              entry={selectedScheduledSend}
              accountEmail={activeAccountLabel}
              busy={scheduledSends.loading}
              onSendNow={() => {
                void scheduledSends.sendNow(selectedScheduledSend.id).then((ok) => {
                  if (ok) setSelectedUid(null);
                });
              }}
              onCancel={() => {
                void scheduledSends.cancel(selectedScheduledSend.id).then((ok) => {
                  if (ok) setSelectedUid(null);
                });
              }}
            />
          ) : isOutboxView && selectedOutboxItem ? (
            <OutboxMessageReader
              item={selectedOutboxItem}
              accountEmail={activeAccountLabel}
              onEdit={() => {
                compose.openComposeFromOutbox(selectedOutboxItem);
                sendQueue.dismiss(selectedOutboxItem.id);
                setSelectedUid(null);
              }}
              onRetry={() => sendQueue.retry(selectedOutboxItem.id)}
              onDismiss={() => {
                sendQueue.dismiss(selectedOutboxItem.id);
                setSelectedUid(null);
              }}
            />
          ) : (
            <MessageReader
              selectedUid={selectedUid}
              message={reader.message}
              loading={reader.loading}
              error={reader.error}
              accountId={readerAccountId}
              folder={readerImapFolder}
              flagged={selectedReaderFlags.flagged}
              favorite={selectedReaderFlags.favorite}
              seen={selectedMessagePreview?.seen ?? reader.message?.seen ?? true}
              bulkSelectionActive={bulk.selectionCount > 0}
              onAction={(actionId) => void bulk.handleMessageAction(actionId)}
              onRetry={() => reader.reload({ force: true })}
              inlineComposeSession={inlineComposeSession}
              composeAccounts={orderedAccounts}
              composeAccountEmail={compose.composeAccount?.email ?? active.email}
              composeSettingsVersion={settingsVersion}
              onUpdateComposeDraft={compose.updateComposeDraft}
              onSaveComposeDraft={compose.handleSaveComposeDraft}
              onDiscardComposeDraft={compose.handleDiscardComposeDraft}
              onCloseComposeSession={compose.closeComposeSession}
              onComposeEnqueue={compose.handleComposeEnqueue}
              onScheduleCompose={compose.handleScheduleCompose}
              composeHandoffSessionId={compose.sendHandoffSessionId}
            />
          )
        }
      />
      </div>
      <ComposeDrawer
        session={compose.composeSession}
        accounts={orderedAccounts}
        accountEmail={compose.composeAccount?.email ?? active.email}
        settingsVersion={settingsVersion}
        onUpdateDraft={compose.updateComposeDraft}
        onSaveDraft={compose.handleSaveComposeDraft}
        onDiscardDraft={compose.handleDiscardComposeDraft}
        onCloseSession={compose.closeComposeSession}
        onEnqueue={compose.handleComposeEnqueue}
        onScheduleSend={compose.handleScheduleCompose}
        onChangeComposeAccount={compose.changeComposeAccount}
        sending={
          compose.sendHandoffSessionId != null &&
          compose.sendHandoffSessionId === compose.composeSession?.id
        }
      />
      <SnoozeModal
        open={bulk.snoozeDialog != null}
        subject={bulk.snoozeDialog?.subject}
        busy={bulk.snoozeBusy}
        onConfirm={(untilIso) => void bulk.confirmSnooze(untilIso)}
        onClose={() => bulk.closeSnoozeDialog()}
      />
      <MailMoveFolderDialog
        open={bulk.moveDialogOpen}
        folders={flattenFoldersForPicker(foldersWithOutbox)}
        currentFolder={selectedFolder}
        title={bulk.pendingActionCount > 1 ? `Déplacer ${bulk.pendingActionCount} messages` : "Déplacer le message"}
        onSelect={(targetFolder) => void bulk.confirmMoveMessages(targetFolder)}
        onClose={() => {
          bulk.closeMoveDialog();
        }}
      />
      {active?.id != null ? (
        <MailFolderStorageDialog
          open={folderStorageOpen}
          accountId={active.id}
          folder={selectedFolder}
          folderLabel={selectedFolderLabel}
          uids={displayMessages.map((m) => m.uid).filter((uid) => uid > 0)}
          onClose={() => setFolderStorageOpen(false)}
        />
      ) : null}
      <MailContextMenu
        open={bulk.contextMenu != null && !isOutboxView}
        x={bulk.contextMenu?.x ?? 0}
        y={bulk.contextMenu?.y ?? 0}
        items={bulk.contextMenuItems}
        onAction={bulk.handleContextMenuAction}
        onClose={() => bulk.closeContextMenu()}
      />
      <CoffreDestFolderPickerModal
        open={bulk.attachPicker?.mode === "coffre"}
        title={`Enregistrer ${bulk.attachPicker?.targets.length ?? 0} message(s) dans le Coffre`}
        confirmLabel="Enregistrer les PJ ici"
        onClose={() => bulk.closeAttachPicker()}
        onConfirm={(folderPath, zone) => bulk.confirmBulkCoffre(folderPath, zone)}
      />
      <CoffreDestFolderPickerModal
        open={bulk.emlArchive != null}
        filename={
          bulk.emlArchive?.targets.length === 1
            ? `${(bulk.emlArchive.targets[0]?.subject || "Message").trim() || "Message"}.eml`
            : undefined
        }
        title={
          (bulk.emlArchive?.targets.length ?? 0) > 1
            ? `Archiver ${bulk.emlArchive?.targets.length} messages dans le Coffre`
            : "Archiver dans le Coffre"
        }
        confirmLabel="Archiver ici"
        onClose={() => bulk.closeEmlArchive()}
        onConfirm={(folderPath, zone) => bulk.confirmArchiveToCoffre(folderPath, zone)}
      />
      <BulkAttachmentProgressModal open={bulk.attachBusy} progress={bulk.attachProgress} />
      <AddressBookModal open={addressBookOpen} onClose={() => setAddressBookOpen(false)} />
      {bootOverlay}
      </>
    </MailContactsProvider>
  );
}
